/**
 * Documentation Registry
 * 
 * This file imports all markdown documentation at build time using webpack's raw-loader.
 * This ensures docs are bundled into the app and don't require runtime fetch calls.
 */

// Getting Started
import introduction from '!raw-loader!./introduction.md'
import quickStart from '!raw-loader!./quick-start.md'
import accountSetup from '!raw-loader!./account-setup.md'

// Trading Features
import charting from '!raw-loader!./charting.md'
import indicators from '!raw-loader!./indicators.md'
import vtIndicators from '!raw-loader!./vt-indicators.md'
import drawingTools from '!raw-loader!./drawing-tools.md'
import drawingParity from '!raw-loader!./drawing-parity.md'
import marketData from '!raw-loader!./market-data.md'
import platformLogic from '!raw-loader!./platform-logic.md'

// Broker Integration
import brokerIntegration from '!raw-loader!./broker-integration.md'
import fyersSetup from '!raw-loader!./fyers-setup.md'
import aliceblueSetup from '!raw-loader!./aliceblue-setup.md'
import upstoxSetup from '!raw-loader!./upstox-setup.md'
import binanceSetup from '!raw-loader!./binance-setup.md'

// Advanced Features
import aiAssistant from '!raw-loader!./ai-assistant.md'
import chartAutomation from '!raw-loader!./chart-to-automation.md'
import strategyBuilder from '!raw-loader!./strategy-builder-doc.md'
import candlescript from '!raw-loader!./candlescript.md'

// API & Development
import apiReference from '!raw-loader!./api-reference.md'
import websocketApi from '!raw-loader!./websocket-api.md'
import errorHandling from '!raw-loader!./error-handling.md'

// Support
import faq from '!raw-loader!./faq.md'
import troubleshooting from '!raw-loader!./troubleshooting.md'
import contactSupport from '!raw-loader!./contact-support.md'

/**
 * Map of slug -> raw markdown content
 */
export const docs = {
  'introduction': introduction,
  'quick-start': quickStart,
  'account-setup': accountSetup,
  'charting': charting,
  'indicators': indicators,
  'vt-indicators': vtIndicators,
  'drawing-tools': drawingTools,
  'drawing-parity': drawingParity,
  'market-data': marketData,
  'platform-logic': platformLogic,
  'broker-integration': brokerIntegration,
  'fyers-setup': fyersSetup,
  'aliceblue-setup': aliceblueSetup,
  'upstox-setup': upstoxSetup,
  'binance-setup': binanceSetup,
  'ai-assistant': aiAssistant,
  'strategy-builder-doc': strategyBuilder,
  'chart-to-automation': chartAutomation,
  'candlescript': candlescript,
  'api-reference': apiReference,
  'websocket-api': websocketApi,
  'error-handling': errorHandling,
  'faq': faq,
  'troubleshooting': troubleshooting,
  'contact-support': contactSupport,
}

/**
 * Navigation structure for the sidebar
 */
export const navigation = [
  {
    group: 'Getting Started',
    items: [
      { text: 'Introduction', link: 'introduction' },
      { text: 'Quick Start', link: 'quick-start' },
      { text: 'Account Setup', link: 'account-setup' }
    ]
  },
  {
    group: 'Trading Features',
    items: [
      { text: 'Charting System', link: 'charting' },
      { text: 'Technical Indicators', link: 'indicators' },
      { text: 'VT Indicators', link: 'vt-indicators' },
      { text: 'Drawing Tools', link: 'drawing-tools' },
      { text: 'Drawing coverage & limits', link: 'drawing-parity' },
      { text: 'Market Data', link: 'market-data' }
    ]
  },
  {
    group: 'Broker Integration',
    items: [
      { text: 'Overview', link: 'broker-integration' },
      { text: 'Fyers Setup', link: 'fyers-setup' },
      { text: 'AliceBlue Setup', link: 'aliceblue-setup' },
      { text: 'Upstox Setup', link: 'upstox-setup' },
      { text: 'Binance Setup', link: 'binance-setup' }
    ]
  },
  {
    group: 'Advanced Features',
    items: [
      { text: 'AI Assistant', link: 'ai-assistant' },
      { text: 'Chart strategies & automation', link: 'chart-to-automation' },
      { text: 'CandleScript', link: 'candlescript' },
      { text: 'Platform Logic Review', link: 'platform-logic' }
    ]
  },
  {
    group: 'Platform Features',
    items: [
      { text: 'Market Data', link: 'market-data' },
      { text: 'Mobile Trading', link: 'websocket-api' },
      { text: 'Desktop App', link: 'api-reference' },
      { text: 'System Status', link: 'error-handling' }
    ]
  },
  {
    group: 'Support',
    items: [
      { text: 'FAQ', link: 'faq' },
      { text: 'Troubleshooting', link: 'troubleshooting' },
      { text: 'Contact Support', link: 'contact-support' }
    ]
  }
]

/**
 * Build search index from all docs
 */
export function buildSearchIndex() {
  const index = []
  
  for (const [slug, content] of Object.entries(docs)) {
    // Extract title from frontmatter or first heading
    const titleMatch = content.match(/^---[\s\S]*?title:\s*(.+?)[\r\n]/) || 
                       content.match(/^#\s+(.+?)[\r\n]/)
    const title = titleMatch ? titleMatch[1].trim() : slug

    // Extract description from frontmatter
    const descMatch = content.match(/^---[\s\S]*?description:\s*(.+?)[\r\n]/)
    const description = descMatch ? descMatch[1].trim() : ''

    // Strip frontmatter and markdown syntax for searchable text
    const plainText = content
      .replace(/^---[\s\S]*?---/, '')  // Remove frontmatter
      .replace(/#{1,6}\s/g, '')        // Remove heading markers
      .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.+?)\*/g, '$1')     // Remove italic
      .replace(/`{1,3}[\s\S]*?`{1,3}/g, '') // Remove code blocks
      .replace(/\[(.+?)\]\(.+?\)/g, '$1')   // Remove links, keep text
      .replace(/[|\-:]/g, ' ')         // Remove table chars
      .replace(/\s+/g, ' ')           // Normalize whitespace
      .trim()
      .substring(0, 500)              // Limit index size

    index.push({
      slug,
      title,
      description,
      content: plainText,
      path: `/help/${slug}`
    })
  }

  return index
}
